import { useCallback, Dispatch, SetStateAction, useMemo, useState, useEffect } from 'react';
import deepEqual from 'fast-deep-equal/es6/react';
import {
  Checkbox,
  CheckboxGroup,
  Stack,
  Button,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Heading,
  Box,
} from '@chakra-ui/react';
import { SkeletonLoader } from 'shared-components';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { IStudentRow } from 'types';
import { differenceWith } from 'lodash';
import { bulkAction, requestSelectedPartners } from '@/store/dashboard/actions';
import { BulkActionTypeEnum } from '@/store/studentUsers/types';
import { useTranslation } from "react-i18next";

interface IPartnerSerialized {
  value: string;
  checked: boolean;
  indeterminate: boolean;
}

const serializePartners = (
  selectedStudents: IStudentRow[],
  partners: string[],
  allSelected = false,
  tabTotalsCount?: number,
  selectedPartnersTotals?: { [key: string]: number },
) => {
  return partners.map(partner => {
    let countOfStudentsWithPartner = 0;

    selectedStudents.forEach(student => {
      if (student.partners.includes(partner)) countOfStudentsWithPartner++;
    });

    const allSelectedStudentsHavePartnerAssigned = countOfStudentsWithPartner === selectedStudents.length;
    const atLeastOneSelectedStudentHavePartner = countOfStudentsWithPartner > 0;
    const selectedPartnerCount = selectedPartnersTotals ? selectedPartnersTotals[partner] : undefined;
    const checked = allSelected ? tabTotalsCount === selectedPartnerCount : allSelectedStudentsHavePartnerAssigned;
    const indeterminate = allSelected
      ? selectedPartnerCount && tabTotalsCount !== selectedPartnerCount
      : !allSelectedStudentsHavePartnerAssigned && atLeastOneSelectedStudentHavePartner;

    return {
      value: partner,
      label: partner,
      checked,
      indeterminate,
    };
  });
};

const serializePartnerChange = (selectedValues: string[], partners: IPartnerSerialized[]) => {
  return partners.map(partner => {
    const isChecked = selectedValues.includes(partner.value);
    partner.checked = isChecked;
    if (isChecked) {
      partner.indeterminate = false;
    }
    return partner;
  });
};

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const EditServicesModal: React.FC<IProps> = ({ isOpen, setIsOpen }) => {
    const { t } = useTranslation('common');
  const students: IStudentRow[] = useSelector(({ dashboard }: ApplicationState) => dashboard.students || [], deepEqual);

  const selectedRows = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedRows, deepEqual);
  const allSelected = useSelector(({ dashboard }: ApplicationState) => dashboard.allSelected, deepEqual);
  const tabs = useSelector(({ dashboard }: ApplicationState) => dashboard.tabs, deepEqual);
  const selectedPartnersLoading = useSelector(
    ({ dashboard }: ApplicationState) => dashboard.selectedPartnersLoading,
    deepEqual,
  );
  const selectedPartnersTotals = useSelector(
    ({ dashboard }: ApplicationState) => dashboard.selectedPartnersTotals,
    deepEqual,
  );
  const tabTotalsCount = tabs.find(({ active }) => active).count || 0;
  const institutionPartners = useSelector(({ config }: ApplicationState) => config.partners || [], deepEqual);

  const dispatch = useDispatch();

  const selectedStudents = useMemo(
    () => students.filter(student => selectedRows.includes(student.p_key)),
    [selectedRows, students],
  );

  const isSelectedPathwayStudents = useMemo(
    () => selectedStudents.every(student => student.pathway),
    [selectedStudents],
  );

  const serializedPartners = useMemo(
    () => serializePartners(selectedStudents, institutionPartners, allSelected, tabTotalsCount, selectedPartnersTotals),
    [
      selectedStudents,
      isSelectedPathwayStudents,
      institutionPartners,
      allSelected,
      tabTotalsCount,
      selectedPartnersTotals,
    ],
  );

  const [partners, setPartners] = useState(
    serializePartners(selectedStudents, institutionPartners, allSelected, tabTotalsCount, selectedPartnersTotals),
  );
  const selectedCheckboxes = useMemo(() => {
    const selectedPartners = [];
    partners.forEach(partner => {
      if (partner.checked) selectedPartners.push(partner.value);
    });
    return selectedPartners;
  }, [partners]);

  const handlePartnerChange = (selectedValues: string[]) => {
    setPartners(prevState => serializePartnerChange(selectedValues, prevState) as typeof partners);
  };

  const handleCloseEditPartnerModal = () => {
    setPartners(serializedPartners);
    setIsOpen(false);
  };

  const handleConfirmEditServicesModal = useCallback(() => {
    const partnersChanged = differenceWith(partners, serializedPartners, deepEqual);
    const partnersToRemove = partnersChanged
      .filter(partner => !partner.checked && !partner.indeterminate)
      .map(partner => partner.value);

    const partnersToAdd = partnersChanged
      .filter(partner => partner.checked && !partner.indeterminate)
      .map(partner => partner.value);
    if (partnersToAdd.length) {
      dispatch(
        bulkAction(
          BulkActionTypeEnum.ADD_PARTNERS,
          partnersToAdd,
          selectedStudents.map(student => student.p_key),
        ),
      );
    }
    if (partnersToRemove.length) {
      dispatch(bulkAction(BulkActionTypeEnum.REMOVE_PARTNERS, partnersToRemove));
    }
    setIsOpen(false);
  }, [dispatch, partners, selectedStudents, serializedPartners, setIsOpen]);

  const handleRemoveAllEditPartnerModal = () => {
    const partnersToRemove = partners
      .filter(partner => partner.checked || partner.indeterminate)
      .map(partner => partner.value);

    if (partnersToRemove.length) {
      dispatch(bulkAction(BulkActionTypeEnum.REMOVE_PARTNERS, partnersToRemove));
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && allSelected) {
      dispatch(requestSelectedPartners());
    }
  }, [dispatch, isOpen, allSelected]);

  useEffect(() => {
    setPartners(
      serializePartners(selectedStudents, institutionPartners, allSelected, tabTotalsCount, selectedPartnersTotals),
    );
  }, [isOpen, allSelected, tabTotalsCount, selectedPartnersTotals, selectedPartnersLoading]);

  return (
    <Modal id="edit-services" onClose={handleCloseEditPartnerModal} isOpen={isOpen} size="sm" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{t('common:editServiceProviders')}</ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          {selectedPartnersLoading ? (
            <Box mt={4} mb={12}>
              <SkeletonLoader />
            </Box>
          ) : (
            <>
              <Heading as="h3" mb="2" fontSize="md">
                {t('common:chooseProviderS')}
                                                </Heading>

              <CheckboxGroup onChange={handlePartnerChange} value={selectedCheckboxes}>
                <Stack spacing={0.5}>
                  {partners.map((partner, i) => {
                    return (
                      <Checkbox
                        value={partner.value}
                        isIndeterminate={partner.indeterminate}
                        key={partner.value + i}
                        isChecked={partner.checked}
                        data-selector={partner.label}
                        aria-checked={partner.checked}
                        variant="ghost"
                      >
                        {partner.label}
                      </Checkbox>
                    );
                  })}
                </Stack>
              </CheckboxGroup>
            </>
          )}
        </ModalBody>
        {!selectedPartnersLoading && (
          <ModalFooter gap="3">
            <Button onClick={handleRemoveAllEditPartnerModal} data-selector="button" size="md" mr="auto">
              {t('common:removeAll')}
                                      </Button>

            <Button onClick={handleCloseEditPartnerModal} data-selector="button" size="md">
              {t('common:cancel')}
                                      </Button>

            <Button onClick={handleConfirmEditServicesModal} data-selector="button" size="md" variant="solid">
              {t('common:apply')}
                                      </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
